import { addDaysYmd, fmtTime, fmtYmdDow, localYmd } from './portalTime';
import { periodContaining } from './timesheetModel';
import type {
  PortalActivityActorRole,
  PortalActivityItem,
  PortalActivityKind,
  PortalActivityResponse,
  PortalActivitySourceType,
  PortalPayPeriod,
} from './portalTypes';

/**
 * Employee Time Portal — My Activity (P-TAS-20 Phase 3, App-4; handoff §9, screenshot 44).
 * Pure helpers over GET /portal/activity: normalize, filter (type / source / search),
 * group by local day, label the actor, merge pages, and find the week before the oldest
 * loaded one for "Load older". Today's Recent activity and My Timesheet's "Changes to this
 * week" read the same feed.
 */

export const RANGE_OPTIONS = [1, 2, 4, 8] as const;
export type RangeWeeks = (typeof RANGE_OPTIONS)[number];
export const DEFAULT_RANGE: RangeWeeks = 2;

/* ------------------------------------------------------------------ normalize */

const KINDS: PortalActivityKind[] = ['punch', 'edit', 'submit', 'approve', 'unlock', 'timeoff'];
const ROLES: PortalActivityActorRole[] = ['you', 'payroll', 'supervisor', 'system', 'kiosk'];
const SOURCES: PortalActivitySourceType[] = ['web', 'mobile', 'kiosk', 'payroll', 'system', 'supervisor', 'employee'];

const str = (v: unknown): string => (typeof v === 'string' ? v : v == null ? '' : String(v));

/** The response, defensively: unknown kinds / roles / sources fall back, bad rows are dropped. */
export function normalizeActivity(raw: unknown): PortalActivityResponse {
  const d = (raw && typeof raw === 'object' ? raw : {}) as { items?: unknown; payPeriodIDs?: unknown };
  const items: PortalActivityItem[] = [];
  for (const r of Array.isArray(d.items) ? d.items : []) {
    if (!r || typeof r !== 'object') continue;
    const x = r as Record<string, any>;
    const id = str(x.id);
    const at = str(x.at);
    if (!id || isNaN(Date.parse(at))) continue;
    const kind = KINDS.includes(x.kind) ? (x.kind as PortalActivityKind) : 'edit';
    const actorRole = ROLES.includes(x.actorRole) ? (x.actorRole as PortalActivityActorRole) : 'system';
    const srcType = SOURCES.includes(x.source?.type) ? (x.source.type as PortalActivitySourceType) : 'system';
    const item: PortalActivityItem = {
      id,
      at,
      kind,
      what: str(x.what),
      detail: str(x.detail),
      actorName: str(x.actorName),
      actorRole,
      source: { type: srcType, label: str(x.source?.label) || SOURCE_FILTER_LABEL[srcType] },
    };
    if (x.delta && typeof x.delta === 'object') item.delta = { before: str(x.delta.before), after: str(x.delta.after) };
    if (typeof x.weekMonday === 'string' && x.weekMonday) item.weekMonday = x.weekMonday.slice(0, 10);
    items.push(item);
  }
  const payPeriodIDs = (Array.isArray(d.payPeriodIDs) ? d.payPeriodIDs : [])
    .map((v) => Number(v))
    .filter((v) => Number.isInteger(v) && v > 0);
  return { items: sortNewestFirst(items), payPeriodIDs };
}

/** Newest first; ties keep their incoming order. */
export function sortNewestFirst(items: PortalActivityItem[]): PortalActivityItem[] {
  return items
    .map((item, index) => ({ item, index, t: Date.parse(item.at) }))
    .sort((a, b) => b.t - a.t || a.index - b.index)
    .map((x) => x.item);
}

/** Append a page to what's loaded: de-duplicated by id (a time-off request can span pages), newest first. */
export function mergeActivity(existing: PortalActivityItem[], incoming: PortalActivityItem[]): PortalActivityItem[] {
  const seen = new Set(existing.map((i) => i.id));
  return sortNewestFirst([...existing, ...incoming.filter((i) => !seen.has(i.id))]);
}

/* ------------------------------------------------------------------ filters */

export type ActivityTypeFilter = 'all' | 'punches' | 'edits' | 'timesheet' | 'timeoff';

export const TYPE_FILTERS: { key: ActivityTypeFilter; label: string }[] = [
  { key: 'all', label: 'All activity' },
  { key: 'punches', label: 'Punches' },
  { key: 'edits', label: 'Edits & corrections' },
  { key: 'timesheet', label: 'Timesheets' },
  { key: 'timeoff', label: 'Time off' },
];

export function typeOfKind(kind: PortalActivityKind): Exclude<ActivityTypeFilter, 'all'> {
  switch (kind) {
    case 'punch': return 'punches';
    case 'edit': return 'edits';
    case 'timeoff': return 'timeoff';
    default: return 'timesheet';
  }
}

/** The source chips, in display order. 'employee' = submits and requests you made (device not recorded). */
export const SOURCE_FILTERS: PortalActivitySourceType[] = ['web', 'mobile', 'kiosk', 'employee', 'payroll', 'supervisor', 'system'];

export const SOURCE_FILTER_LABEL: Record<PortalActivitySourceType, string> = {
  web: 'Web',
  mobile: 'Mobile app',
  kiosk: 'Kiosk',
  employee: 'Employee',
  payroll: 'Payroll',
  supervisor: 'Supervisor',
  system: 'System',
};

export interface ActivityFilter {
  type: ActivityTypeFilter;
  /** Empty = all sources. */
  sources: PortalActivitySourceType[];
  query: string;
}

export const NO_FILTER: ActivityFilter = { type: 'all', sources: [], query: '' };

export function isFiltered(f: ActivityFilter): boolean {
  return f.type !== 'all' || f.sources.length > 0 || f.query.trim() !== '';
}

/** Toggle one source chip; selecting every chip collapses back to "all" (empty). */
export function toggleSource(sources: PortalActivitySourceType[], s: PortalActivitySourceType): PortalActivitySourceType[] {
  const next = sources.includes(s) ? sources.filter((x) => x !== s) : [...sources, s];
  return next.length === SOURCE_FILTERS.length ? [] : SOURCE_FILTERS.filter((x) => next.includes(x));
}

export function filterActivity(items: PortalActivityItem[], f: ActivityFilter): PortalActivityItem[] {
  const q = f.query.trim().toLowerCase();
  return items.filter((i) => {
    if (f.type !== 'all' && typeOfKind(i.kind) !== f.type) return false;
    if (f.sources.length > 0 && !f.sources.includes(i.source.type)) return false;
    if (q) {
      const hay = [i.what, i.detail, i.actorName, i.source.label, i.delta?.before ?? '', i.delta?.after ?? ''].join(' ').toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });
}

/* ------------------------------------------------------------------ labels */

export const ROLE_LABEL: Record<PortalActivityActorRole, string> = {
  you: 'You',
  payroll: 'Payroll',
  supervisor: 'Supervisor',
  system: 'System',
  kiosk: 'Kiosk',
};

/** `You`, or `by Steven Boyd · Payroll` (just `by Payroll` when the name is the role). */
export function actorLine(item: Pick<PortalActivityItem, 'actorName' | 'actorRole'>): string {
  if (item.actorRole === 'you') return 'You';
  const role = ROLE_LABEL[item.actorRole];
  const name = item.actorName.trim();
  if (!name || name.toLowerCase() === role.toLowerCase()) return `by ${role}`;
  return `by ${name} · ${role}`;
}

/**
 * The compact rows' line: `Today 8:19 AM · You · Web`. The source is left out when the
 * actor line already ends with it (`by Steven Boyd · Payroll`, `by System`), so a role is
 * never printed twice.
 */
export function miniLine(item: Pick<PortalActivityItem, 'at' | 'actorName' | 'actorRole' | 'source'>, zone: string, todayYmd: string): string {
  const actor = actorLine(item);
  const src = item.source.label.trim();
  const repeats = !src || actor.toLowerCase().endsWith(src.toLowerCase());
  return [whenLabel(item.at, zone, todayYmd), actor, ...(repeats ? [] : [src])].join(' · ');
}

/** The secondary line: actor · detail (detail omitted when empty). */
export function subLine(item: Pick<PortalActivityItem, 'actorName' | 'actorRole' | 'detail'>): string {
  const detail = item.detail.trim();
  return detail ? `${actorLine(item)} · ${detail}` : actorLine(item);
}

export type ActivityTone = 'info' | 'edit' | 'ok' | 'warn' | 'neutral';

export function toneOf(kind: PortalActivityKind): ActivityTone {
  switch (kind) {
    case 'punch': return 'info';
    case 'edit': return 'edit';
    case 'approve': return 'ok';
    case 'unlock': return 'warn';
    default: return 'neutral';
  }
}

/* ------------------------------------------------------------------ grouping */

export interface ActivityDay {
  ymd: string;
  label: string;
  items: PortalActivityItem[];
}

/** `Thursday, Jul 30, 2026` for a calendar date. */
export function dayHeading(ymd: string): string {
  const d = new Date(`${ymd.slice(0, 10)}T00:00:00Z`);
  if (isNaN(d.getTime())) return ymd;
  return d.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
}

/** Items (newest first) grouped by their local day in `zone`, newest day first. */
export function groupByDay(items: PortalActivityItem[], zone: string): ActivityDay[] {
  const days: ActivityDay[] = [];
  const byYmd = new Map<string, ActivityDay>();
  for (const item of sortNewestFirst(items)) {
    const ymd = localYmd(Date.parse(item.at), zone);
    let day = byYmd.get(ymd);
    if (!day) {
      day = { ymd, label: dayHeading(ymd), items: [] };
      byYmd.set(ymd, day);
      days.push(day);
    }
    day.items.push(item);
  }
  return days.sort((a, b) => (a.ymd < b.ymd ? 1 : a.ymd > b.ymd ? -1 : 0));
}

/** `Today 3:02 PM`, `Yesterday 4:11 PM`, or `Mon, Jul 27 5:45 PM` (Today's compact rows). */
export function whenLabel(atIso: string, zone: string, todayYmd: string): string {
  const ms = Date.parse(atIso);
  const ymd = localYmd(ms, zone);
  const time = fmtTime(ms, zone);
  if (ymd === todayYmd) return `Today ${time}`;
  if (ymd === addDaysYmd(todayYmd, -1)) return `Yesterday ${time}`;
  return `${fmtYmdDow(ymd)} ${time}`;
}

/** The events of one week (My Timesheet's "Changes to this week"). */
export function itemsForWeek(items: PortalActivityItem[], weekMonday: string): PortalActivityItem[] {
  return items.filter((i) => i.weekMonday === weekMonday);
}

/* ------------------------------------------------------------------ paging */

/**
 * The oldest week a page read, and where to look for it: `payPeriodIDs` is newest first, so
 * the oldest is the last. `guessYmd` is a date inside it assuming Mon–Sun weeks (D1), used
 * to fetch the periods around it when it isn't loaded yet.
 */
export function oldestOfPage(anchor: Pick<PortalPayPeriod, 'startYmd'>, payPeriodIDs: number[]): { id: number | null; guessYmd: string } {
  const n = Math.max(1, payPeriodIDs.length);
  return { id: payPeriodIDs.length ? payPeriodIDs[payPeriodIDs.length - 1] : null, guessYmd: addDaysYmd(anchor.startYmd, -7 * (n - 1)) };
}

/** The day before `oldest` starts — the next "Load older" page anchors on the week holding it. */
export function dayBefore(oldest: Pick<PortalPayPeriod, 'startYmd'>): string {
  return addDaysYmd(oldest.startYmd, -1);
}

/** The week before `oldest` in the loaded periods, or null when it isn't loaded (fetch around dayBefore). */
export function periodBefore(periods: PortalPayPeriod[], oldest: Pick<PortalPayPeriod, 'startYmd'>): PortalPayPeriod | null {
  return periodContaining(periods, dayBefore(oldest));
}

/** The pager's `3 weeks loaded`. */
export function loadedWeeksLabel(weekCount: number): string {
  return weekCount === 1 ? '1 week loaded' : `${weekCount} weeks loaded`;
}
